/**
 * Who is signed in.
 *
 * The design repo shipped its own version of this file over a second server —
 * Prisma, JWTs, OAuth, an invitation table — which duplicated an identity
 * layer this platform already had, tested, and shares with the WhatsApp agent.
 * That backend is retired. This file keeps the design's *interface* exactly,
 * so every component that calls `useAuth()` compiles and behaves unchanged,
 * and answers it from `/api/v1/me` and `/api/session`.
 *
 * Three roles, not four. `partner` sees money and can change anything;
 * `team` does the work; `viewer` reads. The design's ADMIN/MANAGER split does
 * not exist here because the studio does not: there are two partners and they
 * are the admins.
 *
 * Nothing here is a permission check. It decides which links to draw and which
 * screens to bother rendering; the server re-decides every request on its own,
 * and a `hasPermission` that returned true would still get a 403.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react';
import { api, ApiError } from '../lib/api';
import type { Me, Preferences } from '../api';
import { setDateFormat } from '../lib/format';

/** PARTNER is shown as Admin: the wire keeps the name the server has always used. */
export type Role = 'PARTNER' | 'MANAGER' | 'TEAM' | 'VIEWER';

export const ROLE_LABEL: Record<Role, string> = {
  PARTNER: 'Admin',
  MANAGER: 'Manager',
  TEAM: 'Team Member',
  VIEWER: 'Viewer'
};
export type UserStatus = 'ACTIVE' | 'INACTIVE';
export type PermissionAction = 'view' | 'create' | 'edit' | 'delete' | 'export';

export interface AuthUser {
  id: string;
  name: string;
  role: Role;
  status: UserStatus;
  /**
   * Which person on the roster this account is.
   *
   * Null for a service token — the WhatsApp agent is not somebody, and the
   * screens that greet a person by name show nothing rather than a fiction.
   * Every "my work" filter keys off this, so a null here means an empty board
   * rather than everybody's board.
   */
  linkedPersonaId: string | null;
  /** The colour the roster gave them, for avatars. */
  color: string;
  /** Effort points a week, or null if nobody has set one. */
  capacity: number | null;
  /** What they do here, in their own words; null until they say. */
  title: string | null;
  service: boolean;
  /** What an Admin has switched off for this role, from the permissions matrix. */
  denied: string[];
  /** Their own settings, as the server holds them. */
  preferences: Preferences;
}

interface AuthContextValue {
  currentUser: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (id: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  hasRole: (...roles: Role[]) => boolean;
  hasPermission: (resource: string, action: PermissionAction) => boolean;
  /** Save some of your own settings; the rest are left as they are. */
  savePreferences: (change: Preferences) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const ROLE: Record<Me['role'], Role> = {
  partner: 'PARTNER',
  manager: 'MANAGER',
  team: 'TEAM',
  viewer: 'VIEWER'
};

/**
 * A stable colour per person, since the roster does not store one.
 *
 * Clients keep a hand-picked colour because the studio learned those over a
 * year and can pick a client out of a list by colour faster than by reading.
 * People are half a dozen faces the studio knows by name, so a hash is enough
 * — what matters is only that it does not change between two renders.
 */
const PALETTE = ['#FC2265', '#3b7dde', '#2f9e57', '#d98e1c', '#7c4dff', '#e0433a', '#0f9b9b'];

export function personColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length]!;
}

function toAuthUser(me: Me): AuthUser {
  return {
    id: me.id,
    name: me.person?.name ?? me.id,
    role: ROLE[me.role],
    status: me.person?.status === 'inactive' ? 'INACTIVE' : 'ACTIVE',
    linkedPersonaId: me.person?.id ?? null,
    color: personColor(me.person?.id ?? me.id),
    capacity: me.person?.capacity ?? null,
    title: me.person?.title ?? null,
    service: me.service,
    denied: me.denied ?? [],
    preferences: me.preferences ?? {}
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const user = toAuthUser(await api.get<Me>('/me'));
      setDateFormat(user.preferences.dateFormat);
      setCurrentUser(user);
    } catch {
      // Any failure here means "not signed in" as far as the UI is concerned.
      // A 401 and an unreachable server look the same from a phone with no
      // signal, and both should land on the sign-in screen rather than on a
      // shell with nothing in it.
      setCurrentUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (id: string, password: string) => {
      // The session route lives outside the versioned prefix, because
      // everything under it is already authenticated by the time it is
      // reached. `../api` knows that; this goes through it directly.
      const { api: request } = await import('../api');
      await request('/session', { method: 'POST', body: { id, password }, unversioned: true });
      await refresh();
    },
    [refresh]
  );

  const logout = useCallback(async () => {
    try {
      const { api: request } = await import('../api');
      await request('/session', { method: 'DELETE', unversioned: true });
    } catch {
      // Even if the call fails, drop the client-side state: a UI that keeps
      // showing a signed-in view the server disagrees with is worse than one
      // that asks for a password again.
    }
    setCurrentUser(null);
  }, []);

  const savePreferences = useCallback(async (change: Preferences) => {
    const saved = await api.patch<{ preferences: Preferences }>('/me', { preferences: change });
    setDateFormat(saved.preferences.dateFormat);
    setCurrentUser((prev) => (prev === null ? prev : { ...prev, preferences: saved.preferences }));
  }, []);

  const hasRole = useCallback(
    (...roles: Role[]) => currentUser !== null && roles.includes(currentUser.role),
    [currentUser]
  );

  /**
   * A hint for the UI, and only that: the server decides.
   *
   * The roles' reach is small enough to write down — an Admin can do
   * anything; a manager everything but money, the roster and the studio's
   * settings; the team the work but not those; a viewer reads. On top of that
   * sit the switches an Admin turned off in the matrix, which `/me` sends as
   * `denied`. Export has no operation behind it, so its switch is the only
   * thing that decides whether an Export button shows.
   */
  const hasPermission = useCallback(
    (resource: string, action: PermissionAction) => {
      if (currentUser === null) return false;
      if (currentUser.role === 'PARTNER') return true;
      if (action === 'export') {
        const key = `export.${resource.toLowerCase().replace(/^content.*/, 'content').replace(/^the month$/, 'month')}`;
        return currentUser.role !== 'VIEWER' && !currentUser.denied.includes(key);
      }
      if (currentUser.role === 'VIEWER') return action === 'view';
      if (resource === 'money' || resource === 'people' || resource === 'settings') {
        return action === 'view' && resource !== 'money';
      }
      if (currentUser.role === 'MANAGER') return true;
      return action !== 'delete';
    },
    [currentUser]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      currentUser,
      isAuthenticated: currentUser !== null,
      isLoading,
      login,
      logout,
      refresh,
      hasRole,
      hasPermission,
      savePreferences
    }),
    [currentUser, isLoading, login, logout, refresh, hasRole, hasPermission, savePreferences]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx === null) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function authErrorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return 'Could not reach the server. Check your connection.';
}
